import { Controller } from '../utils/controller';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YT: any;
  }
}

interface IMetaVideoItem {
  fid: string;
  filemime: string;
  filename: string;
  filesize: string;
  status: string;
  timestamp: string;
  type: string;
  uid: string;
  uri: string;
}

interface IMetaDataVideo {
  fid: IMetaVideoItem | string;
}

interface IMetaData {
  webm_fid?: IMetaDataVideo;
  ogv_fid?: IMetaDataVideo;
  mp4_fid?: IMetaDataVideo;
  youtube?: string;
}

const MODAL_CLASS_LIST_DEFAULT = 'flex flex-col content-start bg-white fixed inset-0 z-[100] p-8 ';
const CLOSE_BUTTON_CLASS_LIST_DEFAULT = 'grid self-end text-3xl hover:text-gray-500';
const CLOSE_BUTTON_CONTENT_DEFAULT = 'X';

export default class extends Controller {
  static targets = ['modal'];
  static requiredTargets = [];

  static values = {
    metadata: Object,
    provider: String,
    url: String,
    id: String,
    modalClassList: String,
    closeButtonClassList: String,
    closeButtonContent: String
  };

  // TARGETS
  declare readonly hasModalTarget: boolean;
  declare modalTarget: HTMLDivElement;
  declare modalVideoController: HTMLDivElement;

  // VALUES
  declare modalClassListValue: string;
  declare closeButtonClassListValue: string;
  declare closeButtonContentValue: string;
  declare providerValue: string;
  declare metadataValue: IMetaData;
  declare idValue: string;
  declare urlValue: string;

  // LOCAL STATE
  private player;

  connect() {
    this.closeModal = this.closeModal.bind(this);
    this.onYouTubeIframeAPIReady = this.onYouTubeIframeAPIReady.bind(this);
  }

  openModal() {
    if (this.providerValue == 'youtube' && this.metadataValue && !this.metadataValue.youtube) {
      return;
    }

    if (this.hasModalTarget) {
      this.showModal();
    } else {
      this.createModal();
    }
  }

  createModal() {
    const body = document.body;
    this.modalVideoController = document.createElement('div');
    this.modalVideoController.setAttribute('data-controller', 'video');
    const modal = document.createElement('div');
    const closeButton = document.createElement('button');

    modal.setAttribute('data-video-target', 'modal');
    modal.classList.value = this.modalClassListValue || MODAL_CLASS_LIST_DEFAULT;

    closeButton.classList.value = this.closeButtonClassListValue || CLOSE_BUTTON_CLASS_LIST_DEFAULT;
    closeButton.innerHTML = this.closeButtonContentValue || CLOSE_BUTTON_CONTENT_DEFAULT;
    closeButton.onclick = this.closeModal;

    body.classList.add('overflow-hidden');

    modal.appendChild(closeButton);
    this.modalVideoController.appendChild(modal);
    body.appendChild(this.modalVideoController);

    if (this.providerValue == 'youtube') {
      const videoPlayer = document.createElement('div');
      const id = this.metadataValue[this.providerValue] || this.idValue;
      videoPlayer.id = `video-player-${id}`;
      modal.appendChild(videoPlayer);
      this.loadYouTubeIframeAPI();
    }

    if (this.providerValue == 'htmlfive') {
      this.player = this.createHTMLVideoEl();
      modal.appendChild(this.player);

      const playerPromise = this.player.current?.play();

      if (playerPromise !== undefined) {
        const isPlaying = (ref: HTMLVideoElement) => {
          return ref.currentTime > 0 && !ref.paused && !ref.ended && ref.readyState > 2;
        };

        playerPromise.catch((error) => {
          const ref = this.player.current;

          if (error.name === 'NotAllowedError' && ref && !isPlaying(ref)) {
            ref.addEventListener('click', function () {
              this.play();
            });
          }
        });
      }
    }
  }

  showModal() {
    const body = document.body;

    body.classList.add('overflow-hidden');
    this.modalTarget.classList.toggle('hidden');
  }

  closeModal() {
    const body = document.body;

    this.pauseVideo();
    body.removeChild(this.modalVideoController);
    body.classList.remove('overflow-hidden');
  }

  onPlayerReady(event) {
    event.target.playVideo();
  }

  pauseVideo() {
    if (this.providerValue === 'youtube') {
      this.player.pauseVideo();
    }

    if (this.providerValue === 'htmlfive') {
      this.player.pause();
    }
  }

  onYouTubeIframeAPIReady() {
    const id = this.metadataValue[this.providerValue] || this.idValue;
    window.YT.ready(() => {
      this.player = new window.YT.Player(`video-player-${id}`, {
        height: '100%',
        width: '100%',
        videoId: id,
        playerVars: {
          autoplay: 1,
          controls: 1,
          enablejsapi: 1,
          fs: 1,
          origin: window.location.origin
        },
        events: {
          onReady: this.onPlayerReady
        }
      });
    });
  }

  loadYouTubeIframeAPI() {
    if (window.YT?.loaded) {
      this.onYouTubeIframeAPIReady();
    } else {
      const body = document.body;
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';

      body.appendChild(tag);

      tag.onload = this.onYouTubeIframeAPIReady;
    }
  }

  createHTMLVideoEl() {
    const { metadataValue, providerValue, urlValue } = this;

    const getUri = (data: IMetaData, provider: string, field: string) => {
      return (
        (data && data[provider]?.[field]?.uri) ||
        (typeof data[field]?.fid === 'string' ? false : data[field]?.fid.uri)
      );
    };

    const mp4 = getUri(metadataValue, providerValue, 'mp4_fid');
    const ogg = getUri(metadataValue, providerValue, 'ogv_fid');
    const webm = getUri(metadataValue, providerValue, 'webm_fid');

    const url = urlValue.replace(/\/$/, '');
    const video = document.createElement('video');

    video.controls = true;
    video.autoplay = true;
    video.setAttribute('width', '100%');
    video.setAttribute('height', '100%');
    video.classList.add('lg:pl-10', 'lg:pr-10', 'lg:pb-10');

    const source = document.createElement('source');

    if (mp4) {
      source.src = url + mp4;
      source.type = 'video/mp4';
    }

    if (ogg) {
      source.src = url + ogg;
      source.type = 'video/ogg';
    }

    if (webm) {
      source.src = url + webm;
      source.type = 'video/webm';
    }

    if (source.src) {
      video.appendChild(source);
    }

    return video;
  }
}
